import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, CENTROS } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageTitle } from "@/components/Badges";
import { CopiaDatos } from "@/components/CopiaDatos";
import { Festivos } from "@/components/Festivos";
import { TarifasEditor } from "@/components/TarifasEditor";
import { GmailConexion } from "@/components/GmailConexion";
import { WebConexion } from "@/components/WebConexion";
import { Section } from "@/components/Fields";

const NewUser = ({ onDone }) => {
  const [f, setF] = useState({ nombre: "", email: "", password: "", role: "staff" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", f);
      toast.success("Usuario creado");
      setF({ nombre: "", email: "", password: "", role: "staff" });
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-[1fr_1fr_1fr_140px_auto] gap-2 mb-4" data-testid="new-user-form">
      <Input data-testid="nu-nombre" placeholder="Nombre" value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} required />
      <Input data-testid="nu-email" type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
      <Input data-testid="nu-password" placeholder="Contraseña (mín. 8)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required />
      <select data-testid="nu-role" className="field-select h-10" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
        <option value="staff">Equipo</option><option value="admin">Administradora</option>
      </select>
      <Button data-testid="nu-submit" type="submit" className="bg-[#1E3A8A] hover:bg-[#1E40AF]"><Plus className="h-4 w-4 mr-1" />Crear</Button>
    </form>
  );
};

const PasswordDialog = ({ u, onClose, onSave }) => {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return setErr("Mínimo 8 caracteres");
    if (pw !== pw2) return setErr("Las contraseñas no coinciden");
    const ok = await onSave(pw);
    if (ok) onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="pw-dialog">
        <DialogHeader><DialogTitle className="font-[Outfit]">Nueva contraseña · {u.nombre}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input data-testid="pw-new" type="password" placeholder="Nueva contraseña (mín. 8)" value={pw} onChange={(e) => { setPw(e.target.value); setErr(""); }} autoFocus />
          <Input data-testid="pw-repeat" type="password" placeholder="Repite la contraseña" value={pw2} onChange={(e) => { setPw2(e.target.value); setErr(""); }} />
          {err && <p className="text-sm text-rose-600" data-testid="pw-error">{err}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" data-testid="pw-save" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Guardar contraseña</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const UserRow = ({ u, me, onDone }) => {
  const [pwOpen, setPwOpen] = useState(false);
  const update = async (patch) => {
    try {
      await api.put(`/users/${u.id}`, { ...u, ...patch });
      toast.success(patch.password ? "Contraseña cambiada" : "Usuario actualizado");
      onDone();
      return true;
    } catch (err) {
      toast.error(errMsg(err));
      return false;
    }
  };
  const resetPw = () => setPwOpen(true);
  return (
    <tr data-testid={`user-row-${u.id}`} className={u.activo ? "" : "opacity-50"}>
      <td className="px-3 py-2 font-medium">{u.nombre}</td>
      <td className="px-3 py-2 text-slate-600">{u.email}</td>
      <td className="px-3 py-2">
        <select className="field-select h-8 w-40" value={u.role} disabled={u.id === me} onChange={(e) => update({ role: e.target.value })} data-testid={`user-role-${u.id}`}>
          <option value="staff">Equipo</option><option value="admin">Administradora</option>
        </select>
      </td>
      <td className="px-3 py-2 space-x-2 whitespace-nowrap">
        {u.role !== "admin" && <Button size="sm" variant="outline" onClick={() => update({ ve_economico: !u.ve_economico })} data-testid={`user-eco-${u.id}`}
          className={u.ve_economico ? "border-emerald-500 text-emerald-700" : ""}>{u.ve_economico ? "✓ Ve la parte económica" : "No ve la parte económica"}</Button>}
        <Button size="sm" variant="outline" onClick={resetPw} data-testid={`user-pw-${u.id}`}>Cambiar contraseña</Button>
        {u.id !== me && <Button size="sm" variant="outline" onClick={() => update({ activo: !u.activo })} data-testid={`user-toggle-${u.id}`}>{u.activo ? "Desactivar" : "Activar"}</Button>}
        {pwOpen && <PasswordDialog u={u} onClose={() => setPwOpen(false)} onSave={(pw) => update({ password: pw })} />}
      </td>
    </tr>
  );
};

const ProfRow = ({ p, onDone }) => {
  const update = async (patch) => {
    await api.put(`/profesionales/${p.id}`, { ...p, ...patch });
    onDone();
  };
  return (
    <tr data-testid={`prof-row-${p.id}`} className={p.activo ? "" : "opacity-50"}>
      <td className="px-3 py-2"><input type="color" value={p.color} onChange={(e) => update({ color: e.target.value })} className="h-6 w-8" /></td>
      <td className="px-3 py-2"><Input defaultValue={p.nombre} onBlur={(e) => e.target.value !== p.nombre && update({ nombre: e.target.value })} className="h-8" /></td>
      <td className="px-3 py-2">
        <select className="field-select h-8 w-40" value={p.centro} onChange={(e) => update({ centro: e.target.value })} data-testid={`prof-centro-${p.id}`}>
          <option value="">Ambos centros</option>{CENTROS.map((c) => <option key={c}>{c}</option>)}
        </select>
      </td>
      <td className="px-3 py-2"><Input type="number" defaultValue={p.orden} onBlur={(e) => Number(e.target.value) !== p.orden && update({ orden: Number(e.target.value) })} className="h-8 w-20" /></td>
      <td className="px-3 py-2"><Input type="tel" defaultValue={p.telefono || ""} placeholder="Sin móvil" data-testid={`prof-tel-${p.id}`} onBlur={(e) => e.target.value.trim() !== (p.telefono || "") && update({ telefono: e.target.value.trim() })} className="h-8 w-36" /></td>
      <td className="px-3 py-2"><Button size="sm" variant="outline" onClick={() => update({ activo: !p.activo })} data-testid={`prof-toggle-${p.id}`}>{p.activo ? "Ocultar de la agenda" : "Mostrar"}</Button></td>
    </tr>
  );
};

export default function Equipo() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [profs, setProfs] = useState([]);
  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/profesionales", { params: { todos: true } }).then((r) => setProfs(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  if (user?.role !== "admin") return <p className="text-sm text-slate-500">Solo las administradoras pueden ver esta página.</p>;

  return (
    <div data-testid="equipo-page" className="space-y-6">
      <PageTitle title="Equipo" subtitle="Usuarios con acceso a la app y columnas de la agenda" />
      <Section title="Usuarios" testId="sec-usuarios">
        <NewUser onDone={load} />
        <table className="w-full text-sm"><tbody className="divide-y divide-slate-100">{users.map((u) => <UserRow key={u.id} u={u} me={user.id} onDone={load} />)}</tbody></table>
      </Section>
      <Section title="Columnas de la agenda (profesionales)" testId="sec-profesionales">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="text-left px-3">Color</th><th className="text-left px-3">Nombre</th><th className="text-left px-3">Centro</th><th className="text-left px-3">Orden</th><th className="text-left px-3">Móvil (WhatsApp)</th><th /></tr></thead>
          <tbody className="divide-y divide-slate-100">{profs.map((p) => <ProfRow key={p.id} p={p} onDone={load} />)}</tbody>
        </table>
      </Section>
      <Section title="Correo del centro (Gmail)" testId="sec-gmail"><GmailConexion /></Section>
      <Section title="Festivos" testId="sec-festivos"><Festivos /></Section>
      <Section title="Conexión con la web" testId="sec-web"><WebConexion /></Section>
      <Section title="Precios y datos del presupuesto" testId="sec-tarifas"><TarifasEditor /></Section>
      <Section title="Copia de datos" testId="sec-copia"><CopiaDatos /></Section>
    </div>
  );
}
